import { useCallback, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";

type Surface = "detail-page";

interface UseCopyWalkthroughPromptArgs {
  walkthroughId: string | null | undefined;
  surface: Surface;
}

/**
 * Fetch the walkthrough brief, write it to the clipboard, and emit a
 * telemetry event. All copy surfaces for walkthrough-level prompts should
 * go through this hook so they stay consistent.
 */
export function useCopyWalkthroughPrompt({
  walkthroughId,
  surface,
}: UseCopyWalkthroughPromptArgs) {
  const utils = trpc.useUtils();
  const recordCopied = trpc.walkthrough.recordBriefCopied.useMutation();
  const [isCopying, setIsCopying] = useState(false);

  const copy = useCallback(async () => {
    if (!walkthroughId) return;
    setIsCopying(true);
    try {
      const { markdown, findingCount } = await utils.walkthrough.brief.fetch({
        walkthroughId,
      });
      await navigator.clipboard.writeText(markdown);
      toast.success("Walkthrough copied as prompt", {
        description: `${findingCount} finding${findingCount === 1 ? "" : "s"} included.`,
      });
      // Fire-and-forget — telemetry failure must not break the user action.
      recordCopied
        .mutateAsync({
          walkthroughId,
          findingCount,
          surface,
        })
        .catch(() => {
          // Ignore. The copy succeeded; telemetry failures are logged server-side.
        });
    } catch (err) {
      // Never surface raw error messages — they can leak tRPC/server internals
      // or DOMException details that don't help the user.
      toast.error(classifyCopyError(err));
    } finally {
      setIsCopying(false);
    }
  }, [walkthroughId, surface, utils.walkthrough.brief, recordCopied]);

  return { copy, isCopying };
}

function classifyCopyError(err: unknown): string {
  if (
    typeof DOMException !== "undefined" &&
    err instanceof DOMException &&
    (err.name === "NotAllowedError" || err.name === "SecurityError")
  ) {
    return "Clipboard access was denied by the browser.";
  }
  if (err instanceof Error && err.name === "TRPCClientError") {
    return "Couldn't load the walkthrough. Please refresh and try again.";
  }
  return "Couldn't copy the walkthrough. Please try again.";
}
